import { Vector3 } from "three";
import type { OBB } from "three/examples/jsm/math/OBB.js";

const DEPTH_EPS = 1e-6;
const TANGENT_EPS = 1e-4;

/** Köşe hangi kutunun. İtme her iki durumda da sürüklenene yazılır. */
export type ContactCornerOwner = "dragged" | "obstacle";

/**
 * Bir köşenin bir kenar parçasının içine girmesi.
 * `dragOutward` sabit kutuyu değil, sürüklenen gövdeyi ayıran birim yöndür.
 */
export interface CornerEdgeContact {
  cornerOwner: ContactCornerOwner;
  corner: Vector3;
  edgeFoot: Vector3;
  depth: number;
  edgeKey: string;
  dragOutward: Vector3;
  /** Sabit parçanın kimliği. Aynı modelin kolları aynı öneki paylaşır. */
  obstacleKey: string;
  /** Köşe içeri girdiyse corner, yalnız kenarlar kesiştiyse edge. */
  kind: "corner" | "edge";
}

interface XzPoint {
  x: number;
  z: number;
}

interface XzFace {
  id: string;
  outward: Vector3;
  tangent: Vector3;
  half: number;
  tangentHalf: number;
}

function localXZ(obb: OBB): { x: Vector3; z: Vector3 } | undefined {
  const e = obb.rotation.elements;
  const x = new Vector3(e[0], 0, e[2]);
  const z = new Vector3(e[6], 0, e[8]);
  if (x.lengthSq() < 1e-8 || z.lengthSq() < 1e-8) return undefined;
  x.normalize();
  z.normalize();
  return { x, z };
}

function xzCorners(obb: OBB, axes: { x: Vector3; z: Vector3 }): XzPoint[] {
  const c = obb.center;
  const hx = obb.halfSize.x;
  const hz = obb.halfSize.z;
  const signs: Array<[number, number]> = [
    [1, 1],
    [-1, 1],
    [-1, -1],
    [1, -1],
  ];
  return signs.map(([sx, sz]) => ({
    x: c.x + axes.x.x * hx * sx + axes.z.x * hz * sz,
    z: c.z + axes.x.z * hx * sx + axes.z.z * hz * sz,
  }));
}

function facesOf(obb: OBB, axes: { x: Vector3; z: Vector3 }): XzFace[] {
  return [
    {
      id: "x:1",
      outward: axes.x.clone(),
      tangent: axes.z,
      half: obb.halfSize.x,
      tangentHalf: obb.halfSize.z,
    },
    {
      id: "x:-1",
      outward: axes.x.clone().multiplyScalar(-1),
      tangent: axes.z,
      half: obb.halfSize.x,
      tangentHalf: obb.halfSize.z,
    },
    {
      id: "z:1",
      outward: axes.z.clone(),
      tangent: axes.x,
      half: obb.halfSize.z,
      tangentHalf: obb.halfSize.x,
    },
    {
      id: "z:-1",
      outward: axes.z.clone().multiplyScalar(-1),
      tangent: axes.x,
      half: obb.halfSize.z,
      tangentHalf: obb.halfSize.x,
    },
  ];
}

function faceDepth(corner: XzPoint, host: OBB, face: XzFace): number {
  const relX = corner.x - host.center.x;
  const relZ = corner.z - host.center.z;
  const normalPos = relX * face.outward.x + relZ * face.outward.z;
  return face.half - normalPos;
}

function isInsideHost(corner: XzPoint, host: OBB, faces: XzFace[]): boolean {
  return faces.every((face) => faceDepth(corner, host, face) >= -TANGENT_EPS);
}

function edgeEndpoints(host: OBB, face: XzFace): [XzPoint, XzPoint] {
  const cx = host.center.x + face.outward.x * face.half;
  const cz = host.center.z + face.outward.z * face.half;
  const tx = face.tangent.x * face.tangentHalf;
  const tz = face.tangent.z * face.tangentHalf;
  return [
    { x: cx - tx, z: cz - tz },
    { x: cx + tx, z: cz + tz },
  ];
}

/** Kenar parçasının karşı kutunun içinde kalan aralığı. Yoksa kenar kesişime girmez. */
function segmentInsideSpan(
  a: XzPoint,
  b: XzPoint,
  host: OBB,
  faces: XzFace[],
): { t0: number; t1: number } | undefined {
  let t0 = 0;
  let t1 = 1;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  for (const face of faces) {
    const nDotD = dx * face.outward.x + dz * face.outward.z;
    const nDotRel =
      (a.x - host.center.x) * face.outward.x +
      (a.z - host.center.z) * face.outward.z;
    const dist = face.half - nDotRel;
    if (Math.abs(nDotD) < 1e-12) {
      if (dist < -TANGENT_EPS) return undefined;
      continue;
    }
    const t = dist / nDotD;
    if (nDotD < 0) {
      if (t > t0) t0 = t;
    } else if (t < t1) {
      t1 = t;
    }
    if (t0 > t1 + 1e-8) return undefined;
  }
  if (t1 - t0 <= 1e-4) return undefined;
  return { t0, t1 };
}

function footOnInsideSpan(
  corner: XzPoint,
  host: OBB,
  face: XzFace,
  guest: OBB,
  guestFaces: XzFace[],
): boolean {
  const [a, b] = edgeEndpoints(host, face);
  const span = segmentInsideSpan(a, b, guest, guestFaces);
  if (!span) return false;
  const ex = b.x - a.x;
  const ez = b.z - a.z;
  const len2 = ex * ex + ez * ez;
  if (len2 < 1e-12) return false;
  const t = ((corner.x - a.x) * ex + (corner.z - a.z) * ez) / len2;
  return t >= span.t0 - TANGENT_EPS && t <= span.t1 + TANGENT_EPS;
}

function pushContact(
  out: CornerEdgeContact[],
  corner: XzPoint,
  host: OBB,
  face: XzFace,
  args: {
    cornerOwner: ContactCornerOwner;
    edgePrefix: string;
    obstacleKey: string;
    y: number;
    dragSign: 1 | -1;
  },
): void {
  const depth = faceDepth(corner, host, face);
  if (depth <= DEPTH_EPS) return;
  const relX = corner.x - host.center.x;
  const relZ = corner.z - host.center.z;
  const tangentPos = relX * face.tangent.x + relZ * face.tangent.z;
  if (Math.abs(tangentPos) > face.tangentHalf + TANGENT_EPS) return;
  const point = new Vector3(corner.x, args.y, corner.z);
  out.push({
    cornerOwner: args.cornerOwner,
    corner: point,
    edgeFoot: point.clone().addScaledVector(face.outward, depth),
    depth,
    edgeKey: `${args.edgePrefix}:${face.id}`,
    dragOutward: face.outward.clone().multiplyScalar(args.dragSign),
    obstacleKey: args.obstacleKey,
    kind: "corner",
  });
}

function contactsOnHost(args: {
  corners: XzPoint[];
  host: OBB;
  faces: XzFace[];
  guest: OBB;
  guestFaces: XzFace[];
  cornerOwner: ContactCornerOwner;
  edgePrefix: string;
  obstacleKey: string;
  y: number;
  /** +1: sürüklenen, sabit kutudan çıkar. -1: sabit köşe içeride, sürüklenen kenarı üstünden çeker. */
  dragSign: 1 | -1;
}): CornerEdgeContact[] {
  const inside = args.corners.filter((corner) =>
    isInsideHost(corner, args.host, args.faces),
  );
  if (!inside.length) return [];
  const crossed = args.faces.filter((face) => {
    const [a, b] = edgeEndpoints(args.host, face);
    return segmentInsideSpan(a, b, args.guest, args.guestFaces) !== undefined;
  });
  if (!crossed.length) return [];
  const out: CornerEdgeContact[] = [];
  for (const corner of inside) {
    for (const face of crossed) {
      if (!footOnInsideSpan(corner, args.host, face, args.guest, args.guestFaces)) {
        continue;
      }
      pushContact(out, corner, args.host, face, args);
    }
  }
  return out;
}

function segmentCross(
  a: XzPoint,
  b: XzPoint,
  c: XzPoint,
  d: XzPoint,
): XzPoint | undefined {
  const rx = b.x - a.x;
  const rz = b.z - a.z;
  const sx = d.x - c.x;
  const sz = d.z - c.z;
  const denom = rx * sz - rz * sx;
  if (Math.abs(denom) < 1e-12) return undefined;
  const qpx = c.x - a.x;
  const qpz = c.z - a.z;
  const t = (qpx * sz - qpz * sx) / denom;
  const u = (qpx * rz - qpz * rx) / denom;
  if (t < -TANGENT_EPS || t > 1 + TANGENT_EPS) return undefined;
  if (u < -TANGENT_EPS || u > 1 + TANGENT_EPS) return undefined;
  return { x: a.x + t * rx, z: a.z + t * rz };
}

/** Karşı kutunun bu kenar düzlemini ne kadar geçtiği. Yön, kenarın kendi dışıdır. */
function penetrationThrough(
  intruder: OBB,
  intruderAxes: { x: Vector3; z: Vector3 },
  host: OBB,
  face: XzFace,
): number {
  const relX = intruder.center.x - host.center.x;
  const relZ = intruder.center.z - host.center.z;
  const centerAlong = relX * face.outward.x + relZ * face.outward.z;
  const support =
    Math.abs(intruderAxes.x.x * face.outward.x + intruderAxes.x.z * face.outward.z) *
      intruder.halfSize.x +
    Math.abs(intruderAxes.z.x * face.outward.x + intruderAxes.z.z * face.outward.z) *
      intruder.halfSize.z;
  return face.half - centerAlong + support;
}

/** Köşe içeride değilken: kesişen kenar çiftleri. 4×4 parça testi. */
function edgeCrossContacts(
  dragged: OBB,
  draggedAxes: { x: Vector3; z: Vector3 },
  draggedFaces: XzFace[],
  obstacle: OBB,
  obstacleAxes: { x: Vector3; z: Vector3 },
  obstacleFaces: XzFace[],
  obstacleKey: string,
  selfToken: string,
  y: number,
): CornerEdgeContact[] {
  const out: CornerEdgeContact[] = [];
  const seen = new Set<string>();
  const push = (
    point: XzPoint,
    face: XzFace,
    owner: ContactCornerOwner,
    edgeKey: string,
  ) => {
    const intruder = owner === "dragged" ? dragged : obstacle;
    const intruderAxes = owner === "dragged" ? draggedAxes : obstacleAxes;
    const host = owner === "dragged" ? obstacle : dragged;
    const depth = penetrationThrough(intruder, intruderAxes, host, face);
    if (depth <= DEPTH_EPS) return;
    if (seen.has(edgeKey)) return;
    seen.add(edgeKey);
    const outward =
      owner === "dragged" ? face.outward.clone() : face.outward.clone().multiplyScalar(-1);
    const from = new Vector3(point.x, y, point.z);
    out.push({
      cornerOwner: owner,
      corner: from,
      edgeFoot: from.clone().addScaledVector(outward, depth),
      depth,
      edgeKey,
      dragOutward: outward,
      obstacleKey,
      kind: "edge",
    });
  };
  for (const draggedFace of draggedFaces) {
    const [da, db] = edgeEndpoints(dragged, draggedFace);
    for (const obstacleFace of obstacleFaces) {
      const [oa, ob] = edgeEndpoints(obstacle, obstacleFace);
      const hit = segmentCross(da, db, oa, ob);
      if (!hit) continue;
      push(hit, obstacleFace, "dragged", `${obstacleKey}:o:${obstacleFace.id}`);
      push(
        hit,
        draggedFace,
        "obstacle",
        `${obstacleKey}:s:${selfToken}:${draggedFace.id}`,
      );
    }
  }
  return out;
}

/**
 * Hangi köşe hangi kenarın içinde. Köşe yoksa kesişen kenarlar.
 * Konum yazmaz, itme seçmez.
 */
export function queryContacts(
  dragged: OBB,
  obstacle: OBB,
  obstacleKey: string,
  selfToken: string,
): CornerEdgeContact[] {
  const draggedAxes = localXZ(dragged);
  const obstacleAxes = localXZ(obstacle);
  if (!draggedAxes || !obstacleAxes) return [];
  const y = dragged.center.y;
  const draggedFaces = facesOf(dragged, draggedAxes);
  const obstacleFaces = facesOf(obstacle, obstacleAxes);
  const corners = [
    ...contactsOnHost({
      corners: xzCorners(dragged, draggedAxes),
      host: obstacle,
      faces: obstacleFaces,
      guest: dragged,
      guestFaces: draggedFaces,
      cornerOwner: "dragged",
      edgePrefix: `${obstacleKey}:o`,
      obstacleKey,
      y,
      dragSign: 1,
    }),
    ...contactsOnHost({
      corners: xzCorners(obstacle, obstacleAxes),
      host: dragged,
      faces: draggedFaces,
      guest: obstacle,
      guestFaces: obstacleFaces,
      cornerOwner: "obstacle",
      edgePrefix: `${obstacleKey}:s:${selfToken}`,
      obstacleKey,
      y,
      dragSign: -1,
    }),
  ];
  if (corners.length > 0) return corners;
  return edgeCrossContacts(
    dragged,
    draggedAxes,
    draggedFaces,
    obstacle,
    obstacleAxes,
    obstacleFaces,
    obstacleKey,
    selfToken,
    y,
  );
}

const PUSH_EPS = 1e-4;

export function dragTranslationFor(contact: CornerEdgeContact): Vector3 {
  return contact.dragOutward.clone().multiplyScalar(contact.depth + PUSH_EPS);
}

/** Aynı kenarda en derin köşe, kenarlar arasında en kısa derinlik. */
export function selectShortestDragPush(contacts: readonly CornerEdgeContact[]): {
  chosen?: CornerEdgeContact;
  translation: Vector3;
} {
  const byEdge = new Map<string, CornerEdgeContact>();
  for (const contact of contacts) {
    const prev = byEdge.get(contact.edgeKey);
    if (!prev || contact.depth > prev.depth) byEdge.set(contact.edgeKey, contact);
  }
  let chosen: CornerEdgeContact | undefined;
  for (const edge of byEdge.values()) {
    if (!chosen || edge.depth < chosen.depth) chosen = edge;
  }
  return {
    chosen,
    translation: chosen ? dragTranslationFor(chosen) : new Vector3(),
  };
}
